import SimpleNavigation from "./SimpleNavigation";
import { Shield, Heart, Users, BookOpen } from "lucide-react";
import logo from "../images/Mascot-logo.png";
import { Link, useNavigate } from "react-router-dom";

export default function AboutUs() {
  const values = [
    {
      icon: <Shield size={20} strokeWidth={1.5} />,
      title: "Privacy First",
      description:
        "Your reading data is yours. We don't sell it, share it, or use it to target you with ads. Period.",
    },
    {
      icon: <Heart size={20} strokeWidth={1.5} />,
      title: "Reader-Centered",
      description:
        'Every feature we build starts with a simple question: "Does this help readers?" No bloat, no distractions—just what you need.',
    },
    {
      icon: <Users size={20} strokeWidth={1.5} />,
      title: "Community Driven",
      description:
        "We believe in the power of small, thoughtful communities. Share reviews, reactions, and recommendations with fellow book lovers.",
    },
    {
      icon: <BookOpen size={20} strokeWidth={1.5} />,
      title: "Made for Readers",
      description:
        "From custom shelves to mood-based discovery, we're building features that celebrate the joy of reading in all its forms.",
    },
  ];

  const navigate = useNavigate();

  const goToBeta = () => {
    navigate("/");
    setTimeout(() => {
      document
        .getElementById("beta-list")
        ?.scrollIntoView({ behavior: "smooth" });
    }, 100);
  };

  return (
    <>
      <SimpleNavigation />
      <section className="bg-primary py-18">
        {/* Hero */}
        <section className="text-center px-6 pt-12 pb-16">
          <img src={logo} alt="Bookshelf" className="w-24 h-24 mx-auto mb-4" />
          <h1 className="text-4xl font-normal tracking-tight text-[#1a1a1a] mb-4">
            About Bookshelf
          </h1>
          <p className="text-base text-gray-600 max-w-sm mx-auto leading-relaxed">
            Your cozy companion for tracking, discovering, and celebrating your
            reading life.
          </p>
        </section>

        {/* Our Story */}
        <section className="">
          <div className="max-w-3xl mx-auto px-6 py-16">
            <h2 className="text-2xl font-normal text-[#1a1a1a] mb-7">
              Our Story
            </h2>
            <p className="text-lg text-gray-600 leading-relaxed mb-4">
              Bookshelf was born from a simple observation: readers needed a
              space that felt like home. We wanted to create an app that
              combined the practicality of modern book tracking with the warmth
              of a personal library—a digital nook where you could curl up and
              explore your reading journey.
            </p>
            <p className="text-lg text-gray-600 leading-relaxed mb-4">
              In a world of cluttered interfaces and data-harvesting, we set out
              to build something different. No ads, no algorithm-pushing
              content. No selling your data. Just a thoughtful tool designed by
              readers, for readers.
            </p>
            <p className="text-lg text-gray-600 leading-relaxed">
              Whether you're tracking your current reads, rediscovering old
              favorites, or searching for your next book based on your mood,
              Bookshelf is here to make your reading life more organized, more
              joyful, and more connected.
            </p>
          </div>
        </section>

        {/* Our Values */}
        <section>
          <div className="max-w-3xl mx-auto px-6 py-16">
            <h2 className="text-2xl font-normal text-[#1a1a1a] mb-9">
              Our Values
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {values.map((value) => (
                <div
                  key={value.title}
                  className="bg-primary border border-accent-light rounded-xl p-7"
                >
                  <div className="w-11 h-11 rounded-xl bg-experience-bg flex items-center justify-center mb-4 text-primary-dark">
                    {value.icon}
                  </div>
                  <h3 className="text-base font-semibold text-[#1a1a1a] mb-2">
                    {value.title}
                  </h3>
                  <p className="text-md leading-7 text-gray-600">
                    {value.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* The Team */}
        <section className="bg-primary">
          <div className="max-w-3xl mx-auto px-6 py-16">
            <h2 className="text-2xl font-normal text-[#1a1a1a] mb-7">
              The Team
            </h2>
            <p className="text-lg text-gray-600 leading-relaxed mb-4">
              Bookshelf is currently in beta, being crafted by a small team of
              dedicated book lovers and developers who believe that reading apps
              should be as delightful as the books they help you discover.
            </p>
            <p className="text-lg text-gray-600 leading-relaxed">
              We're readers first—and consumers of everything from literary
              fiction to sci-fi, from poetry to graphic novels. We understand
              what readers need because we are readers.
            </p>
          </div>
        </section>

        {/* Join Our Beta CTA */}
        <section>
          <div className="max-w-3xl mx-auto px-6 py-16">
            <div className="bg-experience-bg border border-accent-light rounded-2xl px-10 py-12 text-center">
              <h2 className="text-2xl font-normal text-[#1a1a1a] mb-4">
                Join Our Beta
              </h2>
              <p className="text-sm leading-8 text-[#555] max-w-md mx-auto mb-8">
                We're still in beta and actively shaping Bookshelf based on
                feedback from readers like you. Join us and help build the
                reading app you've always wanted.
              </p>
              <Link
                onClick={(e) => {
                  e.preventDefault();
                  goToBeta();
                } }
                className="inline-block bg-accent hover:bg-primary-dark text-white px-7 py-3 rounded-lg text-sm font-medium tracking-wide transition-colors cursor-pointer" to={""}              >
                Sign Up for Beta
              </Link>
            </div>
          </div>
        </section>
      </section>
    </>
  );
}
